// Global Instructions Rule Applied!
//
// "Send documents" on the statement run form (spec A3.14 stage 6;
// docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1-d.2).
//
// Shows what would go out and creates a DRAFT email: one message per
// investor account, from the sender's own mailbox, carrying the account's
// stored documents of the DOCUMENT GROUPS ticked (fund reports, statements)
// for the funds ticked. A fund can be ticked for the groups it can send: every
// current document of it in those groups generated and marked as issued, so
// a fund whose statements are still pending can go for its reports. Where
// the fund merges on send the account gets one PDF per fund for the groups
// in the send - the fund report first, then the capital account
// statement(s), then the financial statements; a reports-only send carries
// the report as its own file. Nothing is sent from here: the draft opens in
// the compose form to be reviewed and queued, under the groups' email
// template (cfg.statement_document_groups).
//
// Parts (d.3): an account whose documents do not fit one mailbox message is
// sent in several emails. The plan packs each account's files into parts
// under the cap, in the run's fund order; the Attachments column reads
// "3 attachments in 2 emails", the Size column is the largest part, the
// sentence under the lists says how many emails are created and who is in
// each, and "Create emails" makes one sibling draft per part - the first
// opens, the others are offered as links. A document that on its own does
// not fit any message (a merged PDF over the cap) cannot be sent by email:
// the account reads Over cap and Create stays disabled until the fund is
// unticked or its Merge On Send switched off. "Out of date" is a warning
// only (owner 2026-10-06): the documents were issued as they are.
//
// The capital call form's Send notices modal is the pattern.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Checkbox, Modal, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { getAccessToken } from '../../../core/auth/authService'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { createStatementCampaign, fetchStatementSendPlan } from '../services/investorStatementsService.ts'
import type { CreatedStatementCampaign } from '../services/investorStatementsService.ts'
import type {
  DocumentGroup,
  InvestorStatementRun,
  InvestorStatementRunCampaign,
  InvestorStatementRunFund
} from '../../../state_manager/data_models/investor_statement_run.ts'
import type {
  InvestorStatementSendPlan,
  SendPlanAccount
} from '../../../state_manager/data_models/investor_statement_send_plan.ts'
import type { RunFundChangesMap } from '../hooks/useRunFundChanges.ts'
import { fileSizeText } from '../utils/runDisplay.ts'

const { Text } = Typography

interface SendStatementsModalProps {
  run: InvestorStatementRun
  open: boolean
  onClose: () => void
  /** The draft was created: the caller opens it and re-reads the run. */
  onCreated: (campaign: CreatedStatementCampaign) => void
  /** "Out of date", per fund of the run (useRunFundChanges). */
  changes: RunFundChangesMap
}

/** "1 To, 2 Cc" for an account's recipients. */
export const planRecipientsText = (account: SendPlanAccount): string => {
  const to = account.recipients.filter((r) => r.role !== 'cc').length
  const cc = account.recipients.length - to
  return [to > 0 ? `${to} To` : null, cc > 0 ? `${cc} Cc` : null].filter(Boolean).join(', ') || '—'
}

/** How many emails an account receives: its parts (one when the plan gave it
 *  none - everything in one email). */
export const accountPartCount = (account: SendPlanAccount): number => Math.max(account.parts.length, 1)

/** "3 attachments" / "1 attachment" / "3 attachments in 2 emails". */
export const planFilesText = (account: SendPlanAccount): string => {
  const parts = accountPartCount(account)
  return `${account.files.length} attachment${account.files.length === 1 ? '' : 's'}${parts > 1 ? ` in ${parts} emails` : ''}`
}

/** "Creates 3 emails: 53 accounts in email 1, 2 in email 2, 1 in email 3."
 *  over the accounts that get an email; "" for a plan of one email. */
export const partsSentence = (plan: InvestorStatementSendPlan, emailed: SendPlanAccount[]): string => {
  const partCount = plan.prog__summary.part_count
  if (partCount <= 1) return ''
  const counts = Array.from({ length: partCount }, (_, i) => {
    const partNo = i + 1
    const n = emailed.filter((a) =>
      a.parts.length === 0 ? partNo === 1 : a.parts.some((p) => p.part_no === partNo)
    ).length
    return partNo === 1 ? `${n} account${n === 1 ? '' : 's'} in email 1` : `${n} in email ${partNo}`
  })
  return `Creates ${partCount} emails: ${counts.join(', ')}.`
}

/** The files of an account that fit no email at all. */
const oversizeFiles = (account: SendPlanAccount) => account.files.filter((f) => f.prog__part_no == null)

/**
 * Why a fund cannot be sent for the document groups ticked, from the run
 * read's per-group states; null when it can. The server's rule
 * (stmt__run_fund_send_blocked_reason): every current document of the fund
 * in those groups generated and issued, and at least one such document. The
 * plan's own answer takes over once it has arrived.
 */
export const fundReasonForGroups = (fund: InvestorStatementRunFund, groupCodes: string[] | null): string | null => {
  // a run read with no document groups (none configured): the fund-wide rule
  if (groupCodes === null) return fund.prog__send_blocked_reason ?? null
  const ticked = fund.prog__groups.filter((g) => groupCodes.includes(g.statement_document_groups__code))
  if (ticked.length === 0) return 'Choose at least one kind of document.'
  const withDocuments = ticked.filter((g) => g.prog__counts.total > 0)
  if (withDocuments.length === 0) return ticked[0].prog__send_blocked_reason ?? null
  return withDocuments.find((g) => g.prog__send_blocked_reason)?.prog__send_blocked_reason ?? null
}

/** The document groups some fund of the run can send now. */
export const sendableGroupCodes = (run: InvestorStatementRun): string[] =>
  run.run.prog__document_groups
    .map((g) => g.statement_document_groups__code)
    .filter((code) => run.funds.some((f) => fundReasonForGroups(f, [code]) === null))

/** The draft statement email of the run that holds a document group, if
 *  any: a second draft for the group would carry the same documents again,
 *  so the group is sent by opening that draft. A draft made before groups
 *  existed (no codes) holds every group. */
export const draftHoldingGroup = (run: InvestorStatementRun, code: string): InvestorStatementRunCampaign | undefined =>
  run.campaigns.find(
    (c) =>
      c.email_campaigns__status === 'draft' &&
      (c.investor_statement_email_campaigns__document_group_codes.length === 0 ||
        c.investor_statement_email_campaigns__document_group_codes.includes(code))
  )

/** The document groups a draft statement email of the run already holds. */
export const draftedGroupCodes = (run: InvestorStatementRun): string[] =>
  run.run.prog__document_groups
    .map((g) => g.statement_document_groups__code)
    .filter((code) => draftHoldingGroup(run, code) !== undefined)

/** The plan carries an investor performance deck (a run-level document,
 *  once per account, with the fund reports). */
export const planHasAccountDocuments = (plan: InvestorStatementSendPlan | null): boolean =>
  !!plan?.accounts.some((a) => a.files.some((f) => f.statements.some((s) => s.prog__kind === 'account_document')))

/** What each account receives, for the sentence under the funds. */
const documentsSentence = (
  groups: DocumentGroup[],
  groupCodes: string[],
  fundCount: number,
  withAccountDocuments = false
): string => {
  const labels = groups
    .filter((g) => groupCodes.includes(g.statement_document_groups__code))
    .map((g) => g.statement_document_groups__code)
  const plural = fundCount === 1 ? '' : 's'
  const parts: string[] = []
  if (labels.includes('reports')) {
    parts.push(
      withAccountDocuments
        ? `the quarterly fund report${plural} and its own performance review, first`
        : `the quarterly fund report${plural}`
    )
  }
  if (labels.includes('statements')) {
    parts.push(`its capital account statement${plural} and the financial statements of each fund`)
  }
  if (parts.length === 0) parts.push('its documents of the kinds ticked')
  return parts.join(' and ')
}

export default function SendStatementsModal({ run, open, onClose, onCreated, changes }: SendStatementsModalProps) {
  const groups = run.run.prog__document_groups
  // more than one kind of document exists: the modal offers the choice
  const offersGroups = groups.length > 1
  // the document groups ticked: the ones some fund can send when the modal
  // opens (every group when none can yet)
  const [groupCodes, setGroupCodes] = useState<string[]>([])
  // the funds ticked: every fund sendable for the groups ticked
  const [fundIds, setFundIds] = useState<string[] | null>(null)
  const [onlyUnsent, setOnlyUnsent] = useState<boolean | null>(null)
  const [plan, setPlan] = useState<InvestorStatementSendPlan | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  // which (groups, funds, choice) the plan shown - or the request in flight
  // - is for. It alone decides whose answer counts: no cancel-on-cleanup,
  // which under StrictMode (mount, cleanup, mount) would drop the only
  // request.
  const asked = useRef<string | null>(null)

  // no document groups configured (a run read from before them): the
  // fund-wide rule, and the plan is asked without groups
  const groupless = groups.length === 0
  const codesOrNull = groupless ? null : groupCodes

  const sendableFor = useCallback(
    (codes: string[] | null) =>
      run.funds.filter((f) => fundReasonForGroups(f, codes) === null).map((f) => f.investor_statement_run_funds__id),
    [run.funds]
  )

  useEffect(() => {
    if (open) {
      asked.current = null
      // the groups some fund can send, less the ones a draft email holds
      const drafted = draftedGroupCodes(run)
      const initial = sendableGroupCodes(run).filter((code) => !drafted.includes(code))
      const codes =
        initial.length > 0
          ? initial
          : groups.map((g) => g.statement_document_groups__code).filter((code) => !drafted.includes(code))
      setGroupCodes(codes)
      setFundIds(sendableFor(groups.length === 0 ? null : codes))
      setOnlyUnsent(null)
      setPlan(null)
      setError(null)
    }
    // the ticks follow the run only when the modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Read what would be sent for the ticks. The first read is made for every
  // document; when some were already sent it is narrowed to the ones not
  // yet sent - sending the same document twice is the choice to make on
  // purpose.
  useEffect(() => {
    if (!open || fundIds === null || (!groupless && groupCodes.length === 0)) return
    const keyOf = (choice: boolean | null) =>
      `${groupCodes.join(',')}|${fundIds.join(',')}|${choice === null ? 'auto' : choice}`
    const key = keyOf(onlyUnsent)
    if (asked.current === key) return
    asked.current = key
    void (async () => {
      setLoading(true)
      setError(null)
      const token = getAccessToken(store.getState().auth)
      const documentGroupCodes = groupless ? undefined : groupCodes
      let narrowed = onlyUnsent ?? false
      let res = await fetchStatementSendPlan(
        run.run.investor_statement_runs__id,
        { runFundIds: fundIds, onlyUnsent: narrowed, documentGroupCodes },
        token
      )
      if (onlyUnsent === null && res.data && res.data.prog__summary.already_sent > 0) {
        narrowed = true
        res = await fetchStatementSendPlan(
          run.run.investor_statement_runs__id,
          { runFundIds: fundIds, onlyUnsent: true, documentGroupCodes },
          token
        )
      }
      // a later tick or choice owns the answer now
      if (asked.current !== key) return
      if (onlyUnsent === null) {
        // the choice the read settled on: the plan shown is already for it
        asked.current = keyOf(narrowed)
        setOnlyUnsent(narrowed)
      }
      setPlan(res.data)
      setError(res.data ? null : res.error || 'Failed to read what would be sent')
      setLoading(false)
    })()
  }, [open, fundIds, onlyUnsent, groupCodes, groupless, run.run.investor_statement_runs__id])

  const toggleGroup = useCallback(
    (code: string, checked: boolean) => {
      setGroupCodes((current) => {
        const set = new Set(current)
        if (checked) set.add(code)
        else set.delete(code)
        const next = groups.map((g) => g.statement_document_groups__code).filter((c) => set.has(c))
        // the funds follow the groups: the ones sendable for the new ticks
        setFundIds(sendableFor(next))
        setOnlyUnsent(null)
        return next
      })
    },
    [groups, sendableFor]
  )

  const toggleFund = useCallback((id: string, checked: boolean) => {
    setFundIds((current) => {
      const set = new Set(current ?? [])
      if (checked) set.add(id)
      else set.delete(id)
      return Array.from(set)
    })
  }, [])

  const accounts = plan?.accounts ?? []
  const noRecipient = accounts.filter((a) => a.recipients.length === 0)
  const onlyCc = accounts.filter((a) => a.recipients.length > 0 && !a.prog__has_to)
  const overCap = accounts.filter((a) => a.prog__over_cap)
  const emailed = accounts.filter((a) => a.recipients.length > 0)
  const blocked = plan?.prog__blocked_reason ?? null
  const noGroup = !groupless && groupCodes.length === 0
  const staleFunds = (plan?.funds ?? []).filter(
    (f) => f.prog__selected && (changes[f.investor_statement_run_funds__id]?.staleCount ?? 0) > 0
  )
  // the email template the draft is made from: the one group's, else the
  // statements group's (owner Q11 - the rule of stmt__email_template_id)
  const templateGroupLabel = useMemo(() => {
    const ticked = groups.filter((g) => groupCodes.includes(g.statement_document_groups__code))
    const template =
      ticked.length === 1 ? ticked[0] : groups.find((g) => g.statement_document_groups__code === 'statements')
    return (template ?? ticked[0])?.statement_document_groups__label.toLowerCase() ?? 'statements'
  }, [groups, groupCodes])

  const handleCreate = useCallback(async () => {
    if (!plan || !fundIds || noGroup) return
    setCreating(true)
    try {
      const token = getAccessToken(store.getState().auth)
      // the groups ticked; a run without groups asks as before
      const res = await (codesOrNull
        ? createStatementCampaign(run.run.investor_statement_runs__id, fundIds, onlyUnsent ?? false, token, codesOrNull)
        : createStatementCampaign(run.run.investor_statement_runs__id, fundIds, onlyUnsent ?? false, token))
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to create the statement email')
        return
      }
      if (res.data.prog__sync_error) {
        const failed = res.data.campaigns.filter((c) => c.prog__sync_error).map((c) => c.email_campaigns__name)
        showErrorToast(
          `${res.data.part_count > 1 ? `${failed.join(', ')} ${failed.length === 1 ? 'was' : 'were'}` : 'The email was'} created but could not be filled: ${res.data.prog__sync_error} Use "Refresh from statement run" on its Recipients tab.`
        )
      } else if (res.data.part_count > 1) {
        toast.success(`${res.data.part_count} statement emails drafted — review each and queue it from the email form`)
      } else {
        toast.success('Statement email drafted — review it and queue it from the email form')
      }
      onCreated(res.data)
    } finally {
      setCreating(false)
    }
  }, [plan, fundIds, noGroup, codesOrNull, onlyUnsent, run.run.investor_statement_runs__id, onCreated])

  const columns: ColumnsType<SendPlanAccount> = [
    { title: 'Account', key: 'account', render: (_: unknown, a) => a.accounts__name || 'Account' },
    {
      title: 'Attachments',
      key: 'files',
      width: 150,
      render: (_: unknown, a) => (
        <Tooltip
          title={a.files.map((f) => (
            <div key={`${f.investor_statement_run_funds__id}-${f.prog__file_name}`}>{f.prog__file_name}</div>
          ))}
        >
          <span>{planFilesText(a)}</span>
        </Tooltip>
      )
    },
    {
      title: 'Size',
      key: 'size',
      width: 150,
      align: 'right',
      render: (_: unknown, a) => (
        <Space size={4}>
          <Tooltip title={accountPartCount(a) > 1 ? `The largest of its ${accountPartCount(a)} emails.` : undefined}>
            <span>{fileSizeText(a.prog__bytes)}</span>
          </Tooltip>
          {a.prog__over_cap ? (
            <Tooltip
              title={`A document of this account is on its own over the mailbox cap of ${fileSizeText(plan?.max_message_bytes ?? 0)}: it cannot be sent by email.`}
            >
              <Tag color='red'>Over cap</Tag>
            </Tooltip>
          ) : null}
        </Space>
      )
    },
    {
      title: 'Recipients',
      key: 'recipients',
      width: 150,
      render: (_: unknown, a) =>
        a.recipients.length === 0 ? (
          <Tag color='gold'>None</Tag>
        ) : (
          <Tooltip
            title={a.recipients.map((r) => (
              <div key={r.contacts__id}>
                {r.role === 'cc' ? 'Cc' : 'To'}: {r.contacts__email}
              </div>
            ))}
          >
            <span>{planRecipientsText(a)}</span>
          </Tooltip>
        )
    }
  ]

  return (
    <Modal
      title={offersGroups ? 'Send documents' : 'Send statements'}
      open={open}
      onCancel={onClose}
      onOk={() => void handleCreate()}
      okText={(plan?.prog__summary.part_count ?? 1) > 1 ? 'Create emails' : 'Create email'}
      confirmLoading={creating}
      okButtonProps={{ disabled: !plan || loading || noGroup || !!blocked || emailed.length === 0 || overCap.length > 0 }}
      width={820}
      destroyOnHidden
    >
      {offersGroups ? (
        <div className='mb-3'>
          <div className='mb-1'>
            <Text strong>Documents</Text>
          </div>
          <Space size={16} wrap>
            {groups.map((group) => {
              const code = group.statement_document_groups__code
              // a draft email holds this kind already: it is sent from there
              const draft = draftHoldingGroup(run, code)
              // no fund of the run can send this kind yet: the first reason
              const reason = draft
                ? `The draft email "${draft.email_campaigns__name || 'Statement email'}" holds the ${group.statement_document_groups__label.toLowerCase()}. Open it from the run instead.`
                : run.funds.some((f) => fundReasonForGroups(f, [code]) === null)
                  ? null
                  : (run.funds.map((f) => fundReasonForGroups(f, [code])).find(Boolean) ?? null)
              return (
                <Tooltip key={code} title={reason ?? undefined}>
                  <span>
                    <Checkbox
                      aria-label={group.statement_document_groups__label}
                      checked={groupCodes.includes(code)}
                      disabled={!!reason}
                      onChange={(e) => toggleGroup(code, e.target.checked)}
                    >
                      {group.statement_document_groups__label}
                      {draft ? (
                        <Tag className='ml-2' color='blue'>
                          In draft email
                        </Tag>
                      ) : reason ? (
                        <Tag className='ml-2' color='gold'>
                          Not issued
                        </Tag>
                      ) : null}
                    </Checkbox>
                  </span>
                </Tooltip>
              )
            })}
          </Space>
        </div>
      ) : null}
      <div className='mb-3'>
        <div className='mb-1'>
          <Text strong>Funds</Text>
        </div>
        <Space direction='vertical' size={2}>
          {run.funds.map((fund) => {
            const id = fund.investor_statement_run_funds__id
            const planned = plan?.funds.find((f) => f.investor_statement_run_funds__id === id)
            // the plan's answer once it is here, the run's per-group states before
            const reason = noGroup
              ? 'Choose at least one kind of document.'
              : (planned?.prog__send_blocked_reason ?? fundReasonForGroups(fund, codesOrNull))
            // merged in THIS send: a reports-only send carries the report as its own file
            const merged = (plan?.accounts ?? []).some((a) =>
              a.files.some((f) => f.investor_statement_run_funds__id === id && f.prog__is_merged)
            )
            const change = changes[id]
            return (
              <Tooltip key={id} title={reason ?? undefined}>
                <span>
                  <Checkbox
                    aria-label={fund.prog__name}
                    checked={!!fundIds?.includes(id)}
                    disabled={!!reason}
                    onChange={(e) => toggleFund(id, e.target.checked)}
                  >
                    {fund.prog__name}
                    {planned && !reason ? (
                      <Text type='secondary'>
                        {' '}
                        · {planned.prog__file_count} attachment{planned.prog__file_count === 1 ? '' : 's'}
                        {merged ? ', merged per investor' : ''}
                      </Text>
                    ) : null}
                    {reason ? (
                      <Tag className='ml-2' color='gold'>
                        Not issued
                      </Tag>
                    ) : null}
                    {change?.status === 'done' && change.staleCount > 0 ? (
                      <Tag className='ml-2' color='gold'>
                        {change.staleCount} out of date
                      </Tag>
                    ) : null}
                  </Checkbox>
                </span>
              </Tooltip>
            )
          })}
        </Space>
      </div>
      {noGroup ? (
        <Alert type='warning' showIcon message='Tick at least one kind of document to send.' />
      ) : !plan ? (
        error ? (
          <Alert type='error' showIcon message='Failed to read what would be sent' description={error} />
        ) : (
          <div className='flex justify-center py-6'>
            <Spin />
          </div>
        )
      ) : (
        <Spin spinning={loading}>
          {blocked ? <Alert className='mb-3' type='warning' showIcon message={blocked} /> : null}
          <div className='mb-3'>
            {plan.prog__summary.part_count > 1 ? (
              <>
                {partsSentence(plan, emailed)} An account whose documents do not fit one email receives them in
                several, each numbered in its subject. The drafts are made from your own mailbox
                {offersGroups ? `, under the ${templateGroupLabel} email template` : ''}.{' '}
              </>
            ) : (
              <>
                A draft email is created for {emailed.length} account{emailed.length === 1 ? '' : 's'}, from your
                own mailbox{offersGroups ? `, under the ${templateGroupLabel} email template` : ''}.{' '}
              </>
            )}
            Each account receives only its own documents for the funds ticked:{' '}
            {offersGroups
              ? documentsSentence(groups, groupCodes, plan.prog__summary.funds, planHasAccountDocuments(plan))
              : `its capital account statement${plan.prog__summary.funds === 1 ? '' : 's'} and the financial statements of each fund`}
            . Nothing is sent yet:{' '}
            {plan.prog__summary.part_count > 1
              ? 'the first email opens for you to review, the others are offered as links, and you queue each from there.'
              : 'the email opens for you to review, and you queue it from there.'}
          </div>
          {plan.prog__summary.already_sent > 0 ? (
            <Checkbox
              className='mb-3'
              aria-label={offersGroups ? 'Only documents not yet sent' : 'Only statements not yet sent'}
              checked={onlyUnsent ?? false}
              onChange={(e) => setOnlyUnsent(e.target.checked)}
            >
              Only {offersGroups ? 'documents' : 'statements'} not yet sent ({plan.prog__summary.already_sent} already
              sent)
            </Checkbox>
          ) : null}
          {staleFunds.length > 0 ? (
            <Alert
              className='mb-3'
              type='warning'
              showIcon
              message={`Some issued ${offersGroups ? 'documents' : 'statements'} are out of date.`}
              description={
                <>
                  {staleFunds.map((f) => (
                    <div key={f.investor_statement_run_funds__id}>
                      {f.prog__name}: {changes[f.investor_statement_run_funds__id]?.staleCount}{' '}
                      {offersGroups ? 'document' : 'statement'}
                      {changes[f.investor_statement_run_funds__id]?.staleCount === 1 ? '' : 's'} differ from the books
                      {offersGroups ? ', the settings or the Period Highlights' : ' or the settings'} as they are now.
                    </div>
                  ))}
                  <div>
                    They are sent as issued. Generate them again and mark the fund as issued to send the current
                    figures.
                  </div>
                </>
              }
            />
          ) : null}
          {overCap.length > 0 ? (
            <Alert
              className='mb-3'
              type='error'
              showIcon
              message={`These accounts have a document over the ${fileSizeText(plan.max_message_bytes)} mailbox cap that cannot be sent by email.`}
              description={
                <>
                  {overCap.map((a) => (
                    <div key={a.accounts__id}>
                      {a.accounts__name}:{' '}
                      {oversizeFiles(a)
                        .map((f) => `${f.prog__file_name} (${fileSizeText(f.prog__bytes)})`)
                        .join(', ')}
                    </div>
                  ))}
                  <div>Untick the fund, or switch Merge On Send off for it so its documents go as separate files.</div>
                </>
              }
            />
          ) : null}
          {noRecipient.length > 0 || onlyCc.length > 0 ? (
            <Alert
              className='mb-3'
              type='warning'
              showIcon
              message={
                noRecipient.length > 0
                  ? 'These investors get no email. Add a contact to the recipients topic.'
                  : 'Some accounts need attention before they can go.'
              }
              description={
                <>
                  {noRecipient.length > 0 ? <div>{noRecipient.map((a) => a.accounts__name).join(', ')}</div> : null}
                  {onlyCc.length > 0 ? (
                    <div>
                      Only Cc recipients, no To: {onlyCc.map((a) => a.accounts__name).join(', ')}. Set a To on the
                      email&apos;s Recipients tab.
                    </div>
                  ) : null}
                </>
              }
            />
          ) : null}
          {accounts.length === 0 ? (
            <Text type='secondary'>No investor is left to send to.</Text>
          ) : (
            <Table<SendPlanAccount>
              size='small'
              pagination={false}
              rowKey='accounts__id'
              columns={columns}
              dataSource={accounts}
              scroll={{ y: 320 }}
            />
          )}
        </Spin>
      )}
    </Modal>
  )
}
